import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Loading from '../../../components/shared/Loading/Loading';
import Button from '../../../components/shared/Button/Button';
import Modal from '../../../components/shared/Modal/Modal';
import StatusBadge from '../../../components/shared/StatusBadge/StatusBadge';
import {
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormTextarea,
  FormHint,
  FormActions,
  ErrorMessage,
} from '../../../components/shared/Form/Form.styles';
import { Panel, Notice, DetailList } from '../../sales/Sales.styles';
import { FilterBar, Pager, EmptyBox } from '../../sales/SalesList.styles';
import PromoterSalesView from '../../../components/promoters/PromoterSalesView';
import PromoterCutsView from '../../../components/promoters/PromoterCutsView';
import {
  PanelTabs,
  ItemList,
  ItemCard,
  Chip,
  LinkButton,
} from '../../../components/promoters/promoters.styles';
import {
  kindLabel,
  linkStatus,
  promoterStatus,
} from '../../../components/promoters/promoterLabels';
import {
  AdminSessionError,
  approvePromoter,
  generatePromoterCuts,
  getAdminPromoterCut,
  listAdminPromoterCuts,
  listAdminPromoterSales,
  listAdminPromoters,
  rejectPromoter,
  setPromoterCommission,
  suspendPromoter,
} from '../../../services/adminPromoters';
import { formatMoney } from '../../../utils/money';
import { formatPercent, parseCommissionPercent } from '../../../utils/commission';
import { formatCalendarDate, formatTimestamp } from '../../../utils/dates';

const REASON_MAX = 500;
const PAGE_SIZE = 25;

const SUB_TABS = [
  { id: 'promotores', label: 'Promotores' },
  { id: 'ventas', label: 'Ventas' },
  { id: 'cortes', label: 'Cortes' },
];

const STATUS_FILTERS = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'suspended', label: 'Suspendidos' },
  { value: 'all', label: 'Todos' },
];

// --- modales ---------------------------------------------------------------

// Aprobar (o reactivar) fijando el porcentaje, o solo cambiar el porcentaje.
// El porcentaje: número mayor que 0 y hasta 100, máximo 2 decimales.
const CommissionModal = ({ promoter, mode, onClose, onDone }) => {
  const [text, setText] = useState(promoter.commissionPercent ? String(promoter.commissionPercent) : '');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const approving = mode === 'approve';

  const title = approving
    ? (promoter.status === 'pending' ? `Aprobar a ${promoter.name}` : `Volver a aprobar a ${promoter.name}`)
    : `Cambiar el porcentaje de ${promoter.name}`;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const parsed = parseCommissionPercent(text);
    if (parsed.error) {
      setError(parsed.error);
      return;
    }
    setBusy(true);
    try {
      const data = approving
        ? await approvePromoter(promoter.id, parsed.value)
        : await setPromoterCommission(promoter.id, parsed.value);
      onDone(data.promoter, data.message);
    } catch (err) {
      if (err instanceof AdminSessionError) {
        onDone(null, null, err);
        return;
      }
      setError(err.message);
      setBusy(false);
      // transición inválida (otro admin ya lo cambió): se actualiza la lista
      if (err.data && err.data.currentStatus) onDone(null, null, null, true);
    }
  };

  return (
    <Modal title={title} onClose={busy ? null : onClose} maxWidth="480px" labelledBy="commission-title">
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
          {approving
            ? 'El promotor podrá pedir vender las actividades de empresas y compartir su enlace en cuanto lo apruebes.'
            : 'El nuevo porcentaje aplica a las ventas nuevas; las ya registradas conservan el porcentaje con el que se hicieron.'}
        </p>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <FormGroup>
          <FormLabel htmlFor="commission-percent">Porcentaje de comisión (%)</FormLabel>
          <FormInput
            id="commission-percent"
            inputMode="decimal"
            autoComplete="off"
            value={text}
            disabled={busy}
            placeholder="Ej. 10 o 12.5"
            onChange={(e) => setText(e.target.value)}
            aria-describedby="commission-hint"
            required
          />
          <FormHint id="commission-hint">Un número mayor que 0 y hasta 100, con máximo 2 decimales. Es sobre el monto de lista de cada venta.</FormHint>
        </FormGroup>
        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant={approving ? 'success' : 'primary'} disabled={busy}>
            {busy ? 'Guardando…' : approving ? 'Aprobar' : 'Guardar porcentaje'}
          </Button>
        </FormActions>
      </form>
    </Modal>
  );
};

// Rechazar (desde pendiente) o suspender (desde aprobado), con motivo opcional que ve el promotor.
const ReasonModal = ({ promoter, action, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const suspend = action === 'suspend';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const run = suspend ? suspendPromoter : rejectPromoter;
      const data = await run(promoter.id, reason.trim());
      onDone(data.promoter, data.message);
    } catch (err) {
      if (err instanceof AdminSessionError) {
        onDone(null, null, err);
        return;
      }
      setError(err.message);
      setBusy(false);
      if (err.data && err.data.currentStatus) onDone(null, null, null, true);
    }
  };

  return (
    <Modal
      title={suspend ? `¿Suspender a ${promoter.name}?` : `¿Rechazar a ${promoter.name}?`}
      onClose={busy ? null : onClose}
      maxWidth="480px"
      labelledBy="reason-title"
    >
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
          {suspend
            ? 'Dejará de poder operar y solo verá su estado. Podrás aprobarlo de nuevo cuando quieras.'
            : 'No podrá operar y solo verá su estado. Podrás aprobarlo más adelante si cambias de opinión.'}
        </p>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}
        <FormGroup>
          <FormLabel htmlFor="status-reason">Motivo (opcional)</FormLabel>
          <FormTextarea
            id="status-reason"
            rows={3}
            maxLength={REASON_MAX}
            value={reason}
            disabled={busy}
            onChange={(e) => setReason(e.target.value)}
          />
          <FormHint>El promotor lo verá en su cuenta y en el correo. {reason.length}/{REASON_MAX}</FormHint>
        </FormGroup>
        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>Cancelar</Button>
          <Button type="submit" variant="danger" disabled={busy}>
            {busy ? 'Guardando…' : suspend ? 'Suspender' : 'Rechazar'}
          </Button>
        </FormActions>
      </form>
    </Modal>
  );
};

// Generar cortes bajo demanda: todas las semanas terminadas con algo pendiente, o una semana concreta.
const GenerateModal = ({ onClose, onDone }) => {
  const [scope, setScope] = useState('all'); // all | week
  const [weekStart, setWeekStart] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (scope === 'week' && !weekStart) {
      setError('Elige un día de la semana que quieres cortar.');
      return;
    }
    setBusy(true);
    try {
      const data = await generatePromoterCuts(scope === 'week' ? weekStart : undefined);
      setResult(data);
      onDone();
    } catch (err) {
      if (err instanceof AdminSessionError) {
        onDone(err);
        return;
      }
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const created = result ? (result.cuts || []).length : 0;

  return (
    <Modal title="Generar cortes semanales" onClose={busy ? null : onClose} maxWidth="500px" labelledBy="generate-title">
      <form onSubmit={handleSubmit}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 14 }}>
          Los cortes se calculan por la <strong>fecha de venta</strong> (hora de México) y solo de semanas que ya terminaron
          (lunes a domingo). Si una semana ya tiene su corte, no se duplica.
        </p>
        {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

        <FormGroup>
          <FormLabel id="generate-scope-label">¿Qué semanas?</FormLabel>
          <div role="radiogroup" aria-labelledby="generate-scope-label" style={{ display: 'grid', gap: 8 }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--text-sm)' }}>
              <input type="radio" name="generate-scope" checked={scope === 'all'} disabled={busy} onChange={() => setScope('all')} style={{ marginTop: 3 }} />
              <span>Todas las semanas terminadas que tengan ventas pendientes de corte</span>
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 'var(--text-sm)' }}>
              <input type="radio" name="generate-scope" checked={scope === 'week'} disabled={busy} onChange={() => setScope('week')} style={{ marginTop: 3 }} />
              <span>Una semana concreta</span>
            </label>
          </div>
        </FormGroup>

        {scope === 'week' && (
          <FormGroup>
            <FormLabel htmlFor="generate-week">Cualquier día de esa semana</FormLabel>
            <FormInput id="generate-week" type="date" value={weekStart} disabled={busy} onChange={(e) => setWeekStart(e.target.value)} required />
          </FormGroup>
        )}

        {result && (
          <Notice role="status">
            <strong>{result.message || 'Listo.'}</strong>
            <div style={{ marginTop: 6 }}>
              {created} {created === 1 ? 'corte nuevo' : 'cortes nuevos'}.
              {(result.weeks || []).map((w) => (
                <div key={w.weekStart}>
                  Semana {formatCalendarDate(w.weekStart)} – {formatCalendarDate(w.weekEnd)}: {w.created} nuevos, {w.existing} ya existían.
                </div>
              ))}
            </div>
          </Notice>
        )}

        <FormActions>
          <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>{result ? 'Cerrar' : 'Cancelar'}</Button>
          <Button type="submit" disabled={busy}>{busy ? 'Generando…' : result ? 'Generar otra vez' : 'Generar cortes'}</Button>
        </FormActions>
      </form>
    </Modal>
  );
};

// --- lista de promotores -----------------------------------------------------

const PromotersList = ({ onSessionError, onChanged }) => {
  const [status, setStatus] = useState('pending');
  const [kind, setKind] = useState('');
  const [searchText, setSearchText] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flash, setFlash] = useState('');
  const [modal, setModal] = useState(null); // { type: 'approve'|'commission'|'reject'|'suspend', promoter }

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await listAdminPromoters({ status, kind, search, page, limit: PAGE_SIZE }));
    } catch (err) {
      if (err instanceof AdminSessionError) {
        onSessionError();
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [status, kind, search, page, onSessionError]);

  useEffect(() => { load(); }, [load]);

  const handleDone = (promoter, message, sessionError, stale) => {
    if (sessionError) {
      onSessionError();
      return;
    }
    if (!stale) {
      setModal(null);
      setFlash(message || `Listo: ${promoter.name} ahora está ${promoterStatus(promoter.status).label.toLowerCase()}.`);
    }
    load();
    onChanged();
  };

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchText.trim());
  };

  const promoters = (data && data.promoters) || [];
  const totalPages = (data && data.totalPages) || 1;

  return (
    <>
      {flash && <Notice role="status">{flash}</Notice>}

      <Panel>
        <h2>Filtros</h2>
        <div role="group" aria-label="Filtrar por estado" style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {STATUS_FILTERS.map((f) => (
            <Button
              key={f.value}
              size="small"
              variant={status === f.value ? 'primary' : 'ghost'}
              aria-pressed={status === f.value}
              onClick={() => { setPage(1); setStatus(f.value); }}
            >
              {f.label}
            </Button>
          ))}
        </div>
        <form onSubmit={submitSearch}>
          <FilterBar style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
            <FormGroup>
              <FormLabel htmlFor="ap-kind">Tipo</FormLabel>
              <FormSelect id="ap-kind" value={kind} onChange={(e) => { setPage(1); setKind(e.target.value); }}>
                <option value="">Todos</option>
                <option value="persona">Persona</option>
                <option value="empresa">Empresa</option>
              </FormSelect>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="ap-search">Buscar</FormLabel>
              <FormInput
                id="ap-search"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Nombre, email, código o negocio"
                autoComplete="off"
              />
            </FormGroup>
            <FormGroup>
              <Button type="submit" variant="secondary">Buscar</Button>
            </FormGroup>
          </FilterBar>
        </form>
      </Panel>

      {error && (
        <ErrorMessage role="alert">
          {error} <LinkButton type="button" onClick={load}>Reintentar</LinkButton>
        </ErrorMessage>
      )}

      {loading && !data ? (
        <Loading text="Cargando promotores..." />
      ) : promoters.length === 0 && !error ? (
        <EmptyBox>
          <h3>{status === 'pending' ? 'No hay promotores pendientes de aprobación' : 'No hay promotores con estos filtros'}</h3>
          <p>Las nuevas solicitudes aparecerán aquí.</p>
        </EmptyBox>
      ) : (
        <>
          <ItemList aria-busy={loading}>
            {promoters.map((p) => {
              const state = promoterStatus(p.status);
              const links = p.links || [];
              return (
                <ItemCard key={p.id}>
                  <div className="head">
                    <span className="title">{p.name}</span>
                    <StatusBadge status={state.status}>{state.label}</StatusBadge>
                  </div>
                  <div className="meta">
                    <Chip>{kindLabel(p.kind)}</Chip> {p.email}
                    {p.phone ? ` · ${p.phone}` : ''}
                    {p.city ? ` · ${p.city}` : ''}
                    {p.kind === 'empresa' && p.empresaId ? ` · empresa #${p.empresaId}` : ''}
                  </div>
                  <DetailList>
                    <div className="row"><dt>Código</dt><dd style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}>{p.referralCode || '—'}</dd></div>
                    <div className="row"><dt>Comisión</dt><dd>{p.commissionPercent ? formatPercent(p.commissionPercent) : 'Sin fijar'}</dd></div>
                    {p.stats && (
                      <div className="row">
                        <dt>Ventas vigentes</dt>
                        <dd>{p.stats.validSales} · comisión {formatMoney(p.stats.commissionAmount)}</dd>
                      </div>
                    )}
                    <div className="row"><dt>Registro</dt><dd>{formatTimestamp(p.createdAt)}</dd></div>
                    {p.reviewedAt && <div className="row"><dt>Revisado</dt><dd>{formatTimestamp(p.reviewedAt)}</dd></div>}
                    {p.lastLoginAt && <div className="row"><dt>Último acceso</dt><dd>{formatTimestamp(p.lastLoginAt)}</dd></div>}
                  </DetailList>
                  {p.statusReason && <div className="note"><strong>Motivo:</strong> {p.statusReason}</div>}
                  {links.length > 0 && (
                    <div className="meta">
                      <strong>Empresas:</strong>{' '}
                      {links.map((l) => `${l.empresa ? l.empresa.businessName : '—'} (${linkStatus(l.status).label.toLowerCase()})`).join(' · ')}
                    </div>
                  )}
                  <div className="actions">
                    {(p.status === 'pending' || p.status === 'rejected' || p.status === 'suspended') && (
                      <Button size="small" variant="success" onClick={() => setModal({ type: 'approve', promoter: p })}>
                        {p.status === 'pending' ? 'Aprobar' : 'Volver a aprobar'}
                      </Button>
                    )}
                    {p.status === 'pending' && (
                      <Button size="small" variant="danger" onClick={() => setModal({ type: 'reject', promoter: p })}>Rechazar</Button>
                    )}
                    {p.status === 'approved' && (
                      <>
                        <Button size="small" variant="secondary" onClick={() => setModal({ type: 'commission', promoter: p })}>Cambiar porcentaje</Button>
                        <Button size="small" variant="danger" onClick={() => setModal({ type: 'suspend', promoter: p })}>Suspender</Button>
                      </>
                    )}
                  </div>
                </ItemCard>
              );
            })}
          </ItemList>

          {totalPages > 1 && (
            <Pager>
              <Button size="small" variant="ghost" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>← Anteriores</Button>
              <span>Página {page} de {totalPages} · {data.total} promotores</span>
              <Button size="small" variant="ghost" disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Siguientes →</Button>
            </Pager>
          )}
        </>
      )}

      {modal && (modal.type === 'approve' || modal.type === 'commission') && (
        <CommissionModal
          promoter={modal.promoter}
          mode={modal.type}
          onClose={() => setModal(null)}
          onDone={handleDone}
        />
      )}
      {modal && (modal.type === 'reject' || modal.type === 'suspend') && (
        <ReasonModal
          promoter={modal.promoter}
          action={modal.type}
          onClose={() => setModal(null)}
          onDone={handleDone}
        />
      )}
    </>
  );
};

// --- panel de promotores del admin ------------------------------------------

const AdminPromoters = ({ onUnauthorized, onChanged }) => {
  const [tab, setTab] = useState('promotores');
  const [directory, setDirectory] = useState([]); // promotores (hasta 100) para los filtros
  const [generating, setGenerating] = useState(false);
  const [cutsVersion, setCutsVersion] = useState(0);

  const handleError = useCallback((err) => {
    if (err instanceof AdminSessionError) {
      onUnauthorized();
      return true;
    }
    return false;
  }, [onUnauthorized]);

  useEffect(() => {
    let cancelled = false;
    listAdminPromoters({ status: 'all', limit: 100 })
      .then((data) => { if (!cancelled) setDirectory(data.promoters || []); })
      .catch((err) => { if (!cancelled) handleError(err); });
    return () => { cancelled = true; };
  }, [handleError]);

  const promoterOptions = useMemo(
    () => directory
      .filter((p) => p.status === 'approved' || p.status === 'suspended')
      .map((p) => ({ id: p.id, name: `${p.name} (${kindLabel(p.kind)})` })),
    [directory]
  );

  const empresaOptions = useMemo(() => {
    const seen = new Map();
    directory.forEach((p) => (p.links || []).forEach((l) => {
      if (l.empresa) seen.set(l.empresa.id, l.empresa.businessName);
    }));
    return Array.from(seen, ([id, name]) => ({ id, name }));
  }, [directory]);

  const loadSales = useCallback((p) => listAdminPromoterSales(p), []);
  const loadCuts = useCallback((p) => listAdminPromoterCuts(p), []);
  const loadCut = useCallback((id) => getAdminPromoterCut(id), []);

  return (
    <>
      <PanelTabs role="tablist" aria-label="Secciones de promotores">
        {SUB_TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`atab-${t.id}`}
            aria-selected={tab === t.id}
            aria-controls="atab-panel"
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </PanelTabs>

      <div id="atab-panel" role="tabpanel" aria-labelledby={`atab-${tab}`}>
        {tab === 'promotores' && <PromotersList onSessionError={onUnauthorized} onChanged={onChanged} />}
        {tab === 'ventas' && (
          <PromoterSalesView
            viewer="admin"
            load={loadSales}
            onError={handleError}
            empresaOptions={empresaOptions}
            promoterOptions={promoterOptions}
          />
        )}
        {tab === 'cortes' && (
          <>
            <Panel>
              <h2>Cortes de todos los promotores</h2>
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', marginBottom: 12, lineHeight: 1.5 }}>
                Los cortes se generan solos al terminar cada semana. Si necesitas uno ya, genéralo aquí.
              </p>
              <Button variant="secondary" onClick={() => setGenerating(true)}>Generar cortes</Button>
            </Panel>
            <PromoterCutsView
              viewer="admin"
              load={loadCuts}
              loadDetail={loadCut}
              onError={handleError}
              reloadKey={cutsVersion}
              empresaOptions={empresaOptions}
              promoterOptions={promoterOptions}
            />
          </>
        )}
      </div>

      {generating && (
        <GenerateModal
          onClose={() => setGenerating(false)}
          onDone={(sessionError) => {
            if (sessionError) {
              onUnauthorized();
              return;
            }
            setCutsVersion((v) => v + 1);
          }}
        />
      )}
    </>
  );
};

export default AdminPromoters;
